import {
  AloftPrice,
  CraftsmanAdjustmentProps,
  PositiveInt,
  getCraftsmanAdjustment,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence, selectCompTypes } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectSubjectDepreciationRatioForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';

const calculateHalfBathAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatio: number | undefined,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
) => {
  const compType = compTypes[compID];
  let subjectImprovements = improvements.subject;

  if (compType === 'arv') {
    subjectImprovements = improvements.subjectArv;
  }
  const compImprovements = improvements[compID];

  const subjectPropertyIdentification = propertyIdentification.subject;

  if (
    isUndefined(subjectImprovements) ||
    isUndefined(compImprovements) ||
    isUndefined(depreciationRatio) ||
    isUndefined(subjectPropertyIdentification)
  ) {
    return undefined;
  }

  const {
    halfBaths = 0 as PositiveInt,
    craftsmanQuality: quality,
    propertyType = 'singleFamilyResidence',
  } = subjectImprovements;
  const { halfBaths: compHalfBaths = 0 as PositiveInt } = compImprovements;
  const {
    address: { PostalCode: zipCode },
  } = subjectPropertyIdentification;

  if (isUndefined(quality) || (quality * 10) % 1 !== 0 || quality < 1 || isUndefined(zipCode)) {
    return undefined;
  }

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);

  return adjCalculator.getHalfBathsAdj({
    subject: { halfBaths, quality, propertyType, zipCode },
    comp: { halfBaths: compHalfBaths },
    depreciationRatio,
  }).adjustment;
};

export const selectHalfBathsAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    (state, id: string) => id,
    selectCompTypes,
  ],
  calculateHalfBathAdj,
);

export const selectHalfBathsAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    selectCompSequence,
    selectCompTypes,
  ],
  (adjustmentVersion, improvements, propertyIdentification, depreciationRatio, compSequence, compTypes) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateHalfBathAdj(
        adjustmentVersion,
        improvements,
        propertyIdentification,
        depreciationRatio,
        propID,
        compTypes,
      );
      if (isDefined(adj)) acc[propID] = adj;
      return acc;
    }, {}),
);
